import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { toast } from 'sonner'

// 電視模板相關欄位（後端回傳的資料形狀，僅列出前端會讀取的欄位）
interface TvTemplateRecord {
  template_id?: number
  [key: string]: unknown
}

interface TvTemplateResponse {
  success: boolean
  data?: TvTemplateRecord
}

export const tvTemplateService = {
  // 建立模板
  createTemplate: async (
    data: { template_name: string; description?: string; provider_id?: number },
    token: string,
  ) => {
    try {
      const response = await apiClient.post<TvTemplateResponse>(
        '/api/tv-templates',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('電視模板已建立')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '建立失敗')
        return error.response.data
      }
      toast.error('建立失敗')
      return { success: false }
    }
  },

  // 更新模板
  updateTemplate: async (
    template_id: number,
    data: { template_name?: string; description?: string; is_active?: boolean },
    token: string,
  ) => {
    try {
      const response = await apiClient.put<TvTemplateResponse>(
        `/api/tv-templates/${template_id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('模板已更新')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('更新失敗')
        return error.response.data
      }
      toast.error('更新失敗')
      return { success: false }
    }
  },

  // 刪除模板
  deleteTemplate: async (template_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/tv-templates/${template_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('模板已刪除')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('刪除失敗')
        return error.response.data
      }
      toast.error('刪除失敗')
      return { success: false }
    }
  },

  // 建立模板 VOD
  createTemplateVod: async (
    template_id: number,
    data: {
      source: string
      vod_name: string
      broadcast_type: string
      order?: number
      is_active?: boolean
    },
    token: string,
  ) => {
    try {
      const response = await apiClient.post<TvTemplateResponse>(
        `/api/tv-templates/${template_id}/vods`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('影片已新增')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '新增失敗')
        return error.response.data
      }
      toast.error('新增失敗')
      return { success: false }
    }
  },

  // 更新模板 VOD
  updateTemplateVod: async (
    template_id: number,
    template_vod_id: number,
    data: {
      source?: string
      vod_name?: string
      order?: number
      is_active?: boolean
    },
    token: string,
  ) => {
    try {
      const response = await apiClient.put<TvTemplateResponse>(
        `/api/tv-templates/${template_id}/vods/${template_vod_id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('影片已更新')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('更新失敗')
        return error.response.data
      }
      toast.error('更新失敗')
      return { success: false }
    }
  },

  // 刪除模板 VOD
  deleteTemplateVod: async (
    template_id: number,
    template_vod_id: number,
    token: string,
  ) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/tv-templates/${template_id}/vods/${template_vod_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('影片已刪除')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('刪除失敗')
        return error.response.data
      }
      toast.error('刪除失敗')
      return { success: false }
    }
  },

  // 重新排序模板 VOD
  reorderTemplateVods: async (
    template_id: number,
    vod_ids: number[],
    token: string,
  ) => {
    try {
      const response = await apiClient.put<TvTemplateResponse>(
        `/api/tv-templates/${template_id}/vods/reorder`,
        { vod_ids },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('排序已更新')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('排序更新失敗')
        return error.response.data
      }
      toast.error('排序更新失敗')
      return { success: false }
    }
  },

  // 套用模板到設備
  applyTemplate: async (
    template_id: number,
    broadcast_device_ids: number[],
    token: string,
  ) => {
    try {
      const response = await apiClient.post<TvTemplateResponse>(
        `/api/tv-templates/${template_id}/apply`,
        { broadcast_device_ids },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('模板已套用')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '套用失敗')
        return error.response.data
      }
      toast.error('套用失敗')
      return { success: false }
    }
  },

  // 同步模板到所有已套用設備
  syncTemplate: async (template_id: number, token: string) => {
    try {
      const response = await apiClient.post<TvTemplateResponse>(
        `/api/tv-templates/${template_id}/sync`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('已同步到所有設備')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '同步失敗')
        return error.response.data
      }
      toast.error('同步失敗')
      return { success: false }
    }
  },

  // 解除設備綁定
  unbindTemplate: async (device_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/tv-templates/devices/${device_id}/unbind`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('已解除綁定')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('解除綁定失敗')
        return error.response.data
      }
      toast.error('解除綁定失敗')
      return { success: false }
    }
  },
}
